import _ from 'lodash';
import * as setup from 'setup/cards';

export function getCards(
    current: Partial<GameCards> | null,
    dealType: GameDealType,
    opponents?: number,
    opponent?: number
) {
    // Prepare cards
    const communityCards = current?.communityCards?.slice() || [];
    const holeCards = current?.holeCards?.slice() || [];
    const opponentsCards = current?.opponentsCards?.slice() || [];

    // Get deck without dealt cards
    const dealtCards = _.flattenDeep([communityCards, holeCards, opponentsCards]);
    const remainingCards = _.difference(setup.cards.deck, dealtCards);
    const deck = _.shuffle(remainingCards);

    // Get community flop cards
    if (
        ['all', 'community', 'flop'].includes(dealType) ||
        (['turn', 'river', 'remaining'].includes(dealType) && !communityCards[0])
    ) {
        const flopCards = deck.splice(0, 3);
        communityCards.splice(0, 5, ...flopCards);
    }

    // Get community turn card
    if (
        ['all', 'community', 'laterStreets', 'turn'].includes(dealType) ||
        (['river', 'remaining'].includes(dealType) && !communityCards[3])
    ) {
        const turnCards = deck.splice(0, 1);
        communityCards.splice(3, 2, ...turnCards);
    }

    // Get community river card
    if (
        ['all', 'community', 'laterStreets', 'river'].includes(dealType) ||
        (dealType === 'remaining' && !communityCards[4])
    ) {
        const riverCards = deck.splice(0, 1);
        communityCards.splice(4, 1, ...riverCards);
    }

    // Get hole cards
    if (['all', 'players', 'hole'].includes(dealType) || (dealType === 'remaining' && !holeCards.length)) {
        const deckHoleCards = deck.splice(0, 2);
        holeCards.splice(0, 2, ...deckHoleCards);
    }

    // Get opponent cards
    if (['all', 'players', 'opponent', 'remaining'].includes(dealType)) {
        const count = opponents || opponentsCards.length || 1;
        opponentsCards.splice(count, opponentsCards.length - count);
        for (let i = 0; i < count; i++) {
            const opponentCards = opponentsCards[i];
            if (dealType === 'remaining' ? !opponentCards?.length : opponent === undefined || i === opponent) {
                opponentsCards[i] = deck.splice(0, 2);
            }
        }
    }

    // Return cards
    return {
        communityCards,
        holeCards,
        opponentsCards,
    };
}

export function getCardBaseValues(cards?: GameCardType[]) {
    if (!cards?.length) {
        return '';
    }
    const { valueMultiplier, values } = setup.cards;
    return cards.map(card => values[card] / valueMultiplier).join('+');
}

export function getCardsSides(round: GameRound, opponents: number, opponentsSpied: boolean[]) {
    // Prepare sides
    let communityCardsSides: GameCardSide[] = [];
    let holeCardsSide: GameCardSide | null = 'front';
    let opponentsCardsSides: GameCardSide[] = _.range(opponents).map(index =>
        opponentsSpied[index] ? 'front' : 'back'
    );

    // Update based on round
    if (round === 'init') {
        holeCardsSide = null;
        opponentsCardsSides = [];
    } else if (round === 'hole') {
        holeCardsSide = 'back';
    } else if (round === 'flop') {
        communityCardsSides = ['back', 'back', 'back'];
    } else if (round === 'turn') {
        communityCardsSides = ['front', 'front', 'front', 'back'];
    } else if (round === 'river') {
        communityCardsSides = ['front', 'front', 'front', 'front', 'back'];
    } else if (round === 'opponents') {
        communityCardsSides = ['front', 'front', 'front', 'front', 'front'];
    } else if (round === 'next' || round === 'end') {
        communityCardsSides = ['front', 'front', 'front', 'front', 'front'];
        opponentsCardsSides = new Array(opponentsCardsSides.length).fill('front');
    }

    // Return sides
    return {
        communityCardsSides,
        holeCardsSide,
        opponentsCardsSides,
    };
}

export function getCardNames(cards?: GameCardType[]) {
    if (!cards || cards.length === 0) {
        return '';
    }

    const [first, second] = cards.map(value => {
        const name = setup.cards.names[value];
        const article = name.startsWith('ace') || name.startsWith('eight') ? 'an' : 'a';
        return `${article} ${name}`;
    });

    return `${first} and ${second}`;
}

export function filterRoundCards(current: Partial<GameCards> | null, round: GameRound) {
    // Prepare cards
    const cards = {
        holeCards: current?.holeCards?.slice() || [],
        communityCards: current?.communityCards?.slice() || [],
        opponentsCards: current?.opponentsCards?.slice() || [],
    };

    // Filter hole cards
    if (round === 'hole') {
        cards.holeCards = [];
    }

    // Filter community cards
    if (round === 'hole' || round === 'flop') {
        cards.communityCards = [];
    } else if (round === 'turn') {
        cards.communityCards = cards.communityCards.slice(0, 3);
    } else if (round === 'river') {
        cards.communityCards = cards.communityCards.slice(0, 4);
    }

    // Filter opponents cards
    if (round !== 'end' && round !== 'next') {
        cards.opponentsCards = cards.opponentsCards.map(() => []);
    }

    return cards;
}
